import { useState } from 'react';
import axios from 'axios';
import { User } from 'interfaces/models/user';
import api from 'services/ApiService';
import { isMobile, isBrowser, isTablet } from 'react-device-detect'; // Importe da biblioteca
import apiAuth from 'services/ApiAuthService';
import { showToastError, showToastSuccess } from 'utils/toasts';

interface AuthHook {
  user: User | null;
  setUser: (user: User | null) => void;
  token: string;
  isLogged: boolean;
  login: (email: string, password: string, code: string|undefined|null) => Promise<LoginResponse>;
  logout: () => Promise<void>;
  forgetPassword: (email: string) => Promise<any>;
  resetPassword: (email: string, code: string, newPassword: string) => Promise<any>;
  register: (request: SignupRequest) => Promise<User>;
  getUserLocalStotage: () => Promise<User>;
  resendEmailVerification: () => Promise<any>;
  contactRadar: (request: ContactRadarRequest) => Promise<any>;
}

interface SignupRequest {
  name: string;
  last_name: string;
  display_name: string;
  document: string;
  document_type_id: number;
  birth_date: string;
  email: string;
  phone: string;
  password: string;
  passwordConfirmation: string;
  linkedin?: string;
  accept_terms: boolean;
  was_invited: boolean | undefined;
  instagram?: string;
  facebook?: string;
  x?: string;
  youtube?: string;
  tiktok?: string;
  group_city_id: number | undefined;

  address: string;
  number: number;
  complement: string | undefined,
  district: string;
  country: string;
  state: string;
  city: string;
  postal_code: string | undefined;

  companyName: string | undefined;
  activity: string | undefined;
  website: string | undefined;
  position: string | undefined;

  name_inviter: string | undefined;

  interests: any;
}

interface ContactRadarRequest {
  name: string;
  email: string;
  phone: string;
  message: string;
  // linkedin: string | undefined;
  // facebook: string | undefined;
  // instagram: string | undefined;
  state: string | undefined;
  city: string | undefined;
  country: string | undefined;
}

interface LoginResponse {
  user: User;
  token: string | null;
  status: boolean | undefined;
  message: string | undefined;
  require_email: boolean | undefined;
}

function useAuth(): AuthHook {

  const [user, setUser] = useState<User | null>(null);

  // Pegar deviceName com dados do navegador
  let deviceType = 'Unknown Device';

  if (isMobile) {
    deviceType = 'Mobile';
  } else if (isTablet) {
    deviceType = 'Tablet';
  } else if (isBrowser) {
    deviceType = 'Browser';
  }

  const deviceName = `${deviceType} - ${navigator.userAgent}`;

  // Recupere o token do localStorage quando o hook for inicializado
  const [token, setToken] = useState(localStorage.getItem('token') || '');
  const [isLogged, setIsLogged] = useState<boolean>(!!user && !!token);

  const login = async (email: string, password: string, code: string|undefined|null): Promise<LoginResponse> => {
    try {
      const codeAsInt = code ? parseInt(code, 10) : undefined;

      const response = await api.post('/login', {
        email,
        password,
        device_name: deviceName,
        code: codeAsInt
      });

      const { user, token, status, message } = response.data;

      if (!status) {
        if (message) showToastError(message);
      } else {
        setUser(user);
        setToken(token);
  
        // Salve o token no localStorage
        localStorage.setItem('userData', JSON.stringify(user));
        localStorage.setItem('token', token);
      }

      return response.data;

    } catch (error) {
      throw new Error('Algo de errado aconteceu, aguarde alguns minutos e tente novamente...');
    }
  };

  const logout = async (): Promise<void> => {
    try {
      await apiAuth.post('/logout');
      
      // Limpe o token do localStorage, usuário e token do estado
      localStorage.removeItem('token');
      localStorage.removeItem('userData');
      setUser(null);
      setToken('');
      
    } catch (error) {
      console.error('Erro ao fazer logout:', error);
      throw new Error('Erro ao fazer logout');
    }
  };

  const resendEmailVerification = async (email: string): Promise<void> => {
    try {
      const response = await api.post('/email/resend' , { email });
      
      return response.data;
    } catch (error) {
      console.error('Erro ao reenviar email de verificação:', error);
      throw new Error('Erro ao reenviar email de verificação');
    }
  };

  // função getUserLocalStotage que devolve o usuário do localStorage
  const getUserLocalStotage = async (): Promise<User> => {
    try {
      const userData = localStorage.getItem('userData');
      return JSON.parse(userData || '{}');

    } catch (error) {
      throw new Error('Erro ao recuperar usuário do localStorage');
    }
  };

  // função forgetPassword
  const forgetPassword = async (email: string): Promise<void> => {
    try {
      const response = await api.post('/forgot-password', { email });

      console.log('responseForgetPassword', response.data);
      return response.data;

    } catch (error) {
      throw new Error('Erro ao enviar email de recuperação de senha');
    }
  };

  // função resetPassword
  const resetPassword = async (email: string, code: string, newPassword: string): Promise<void> => {
    try {
      const response = await api.post('/reset-password', { email, code, password: newPassword});

      return response.data;

    } catch (error) {
      throw new Error('Erro ao resetar a senha');
    }
  };

  // função signup
  const register = async (request: SignupRequest): Promise<any> => {
    // try {
      let data = {
        'user': {
          name: request.name,
          last_name: request.last_name,
          display_name: request.display_name,
          email: request.email,
          password: request.password,
          password_confirmation: request.passwordConfirmation,
          document: request.document,
          document_type_id: request.document_type_id,
          birth_date: request.birth_date,
          phone: request.phone,
          linkedin: request.linkedin ?? null,
          was_invited: request.was_invited,
          accept_terms: request.accept_terms,
          interests: request.interests,
          instagram: request.instagram ?? null,
          facebook: request.facebook ?? null,
          x: request.x ?? null,
          youtube: request.youtube ?? null,
          tiktok: request.tiktok ?? null,
          group_city_id: request.group_city_id,
        },
        'interests': request.interests,
        'address': {
          address: request.address,
          number: request.number,
          complement: request.complement,
          district: request.district,
          country: request.country,
          state: request.state,
          city: request.city,
          postal_code: request.postal_code ?? null,
        },
        'recommendation': {
          name_inviter: request.name_inviter,
        },
      };

      if (request.companyName) {
        data = {
          ...data,
          'company': {
            companyName: request.companyName,
            activity: request.activity,
            website: request.website,
            position: request.position,
          },
        }
      }

      const response = await api.post('/register', data);

      return response;

      // const { user, token } = response.data;
      // console.log('userRegister', user)
      // console.log('tokenRegister', token)
      // Salve o token no localStorage

      // localStorage.setItem('token', token);
      // localStorage.setItem('userData', JSON.stringify(user));

      // setUser(user);
      // setToken(token);

      // return user;
    // } catch (error) {
    //   throw new Error('Erro ao cadastrar usuário');
    // }
  };

  const contactRadar = async (request: ContactRadarRequest): Promise<void> => {
    try {
      let data = {
        name: request.name,
        email: request.email,
        phone: request.phone,
        message: request.message,
        // linkedin: request.linkedin,
        // facebook: request.facebook,
        // instagram: request.instagram,
        state: request.state,
        city: request.city,
        country: request.country,
      };

      const response = await api.post('/contact-radar', data);

    } catch (error) {
      throw new Error('Erro ao cadastrar formuláro de contato');
    }
  };

  return {
    user,
    setUser,
    token,
    isLogged,
    login,
    logout,
    register,
    forgetPassword,
    resetPassword,
    getUserLocalStotage,
    resendEmailVerification,
    contactRadar,
  };
}

export default useAuth;